import { cn, SurfaceInspectButton, SurfaceInspectionScope, Dialog, DialogContent, DialogTitle, DialogDescription } from './chunk-UCBSVPDO.mjs';
export { Badge, Button, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, SurfaceDetailData, SurfaceInspectButton, SurfaceInspectionContext, SurfaceInspectionScope, badgeVariants, buttonVariants, cn } from './chunk-UCBSVPDO.mjs';
import { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { OctagonAlert, TriangleAlert, ShieldAlert, ShieldCheck, Loader2, CircleAlert, Check, ChevronRight, X, Search, ArrowRight, ArrowDownRight, ArrowUpRight } from 'lucide-react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import * as SeparatorPrimitive from '@radix-ui/react-separator';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import * as VisuallyHiddenPrimitive from '@radix-ui/react-visually-hidden';
import { Command as Command$1 } from 'cmdk';
import { Toaster as Toaster$1 } from 'sonner';
export { toast } from 'sonner';

var iconButtonVariants = cva(
  "inline-flex shrink-0 items-center justify-center rounded-full transition-[transform,background-color,border-color,color,box-shadow] duration-200 active:scale-95 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        ghost: "text-muted-foreground hover:bg-white/[0.08] hover:text-foreground",
        outline: "border border-white/15 text-foreground hover:border-primary/60 hover:bg-primary/10",
        solid: "bg-gradient-primary text-primary-foreground shadow-[0_6px_18px_-6px_hsl(var(--primary)/0.75),inset_0_1px_0_hsl(0_0%_100%/0.3)] hover:brightness-110"
      },
      size: {
        sm: "h-7 w-7",
        md: "h-7 w-7",
        lg: "h-10 w-10"
      }
    },
    defaultVariants: {
      variant: "ghost",
      size: "md"
    }
  }
);
var IconButton = forwardRef(
  ({ className, variant, size, label, type = "button", ...props }, ref) => /* @__PURE__ */ jsx(
    "button",
    {
      ref,
      type,
      "aria-label": label,
      title: label,
      className: cn(iconButtonVariants({ variant, size }), className),
      ...props
    }
  )
);
IconButton.displayName = "IconButton";
var Card = forwardRef(
  ({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
    "div",
    {
      ref,
      "data-data-surface": "card",
      "data-native-inspection": props.onClick ? "" : void 0,
      className: cn("glass hx-data-surface text-card-foreground rounded-lg", className),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx(SurfaceInspectButton, { native: Boolean(props.onClick), reserveSpace: true })
      ]
    }
  )
);
Card.displayName = "Card";
var CardHeader = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn("hx-section-header flex flex-col gap-1.5 p-6", className),
      ...props
    }
  )
);
CardHeader.displayName = "CardHeader";
var CardTitle = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "h3",
    {
      ref,
      className: cn(
        "hx-section-title text-lg font-semibold leading-none tracking-tight",
        className
      ),
      ...props
    }
  )
);
CardTitle.displayName = "CardTitle";
var CardDescription = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("p", { ref, className: cn("text-muted-foreground text-sm", className), ...props }));
CardDescription.displayName = "CardDescription";
var CardContent = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("p-6 pt-0", className), ...props })
);
CardContent.displayName = "CardContent";
var CardFooter = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("flex items-center p-6 pt-0", className), ...props })
);
CardFooter.displayName = "CardFooter";
var surfaceVariants = cva("hx-data-surface rounded-lg text-card-foreground", {
  variants: {
    variant: {
      default: "glass",
      raised: "glass shadow-elevation-3",
      glass: "glass-strong",
      outline: "border border-white/10 bg-transparent",
      inset: "glass-subtle"
    },
    elevation: {
      none: "",
      1: "shadow-elevation-1",
      2: "shadow-elevation-2",
      3: "shadow-elevation-3",
      4: "shadow-elevation-4"
    },
    padding: {
      none: "p-0",
      sm: "p-3",
      md: "p-4",
      lg: "p-6"
    },
    interactive: {
      true: "glass-hover cursor-pointer",
      false: ""
    }
  },
  defaultVariants: {
    variant: "default",
    elevation: "none",
    padding: "md",
    interactive: false
  }
});
var Surface = forwardRef(
  ({ className, children, variant, elevation, padding, interactive, ...props }, ref) => /* @__PURE__ */ jsxs(
    "div",
    {
      ref,
      "data-data-surface": "surface",
      "data-native-inspection": props.onClick ? "" : void 0,
      className: cn(surfaceVariants({ variant, elevation, padding, interactive }), className),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx(SurfaceInspectButton, { native: Boolean(props.onClick) })
      ]
    }
  )
);
Surface.displayName = "Surface";
var riskConfig = {
  low: {
    label: "Low",
    className: "border-risk-low/30 bg-risk-low/[0.13] text-risk-low",
    icon: ShieldCheck
  },
  moderate: {
    label: "Moderate",
    className: "border-risk-moderate/30 bg-risk-moderate/[0.13] text-risk-moderate",
    icon: ShieldAlert
  },
  high: {
    label: "High",
    className: "border-risk-high/30 bg-risk-high/[0.13] text-risk-high",
    icon: TriangleAlert
  },
  critical: {
    label: "Critical",
    className: "border-risk-critical/40 bg-risk-critical/[0.14] text-risk-critical shadow-glow-critical",
    icon: OctagonAlert
  }
};
var RiskBadge = forwardRef(
  ({ level, hideIcon, label, className, ...props }, ref) => {
    const config = riskConfig[level];
    const Icon = config.icon;
    const text = label ?? config.label;
    return /* @__PURE__ */ jsxs(
      "span",
      {
        ref,
        className: cn(
          "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium",
          "bg-opacity-80 shadow-elevation-1",
          config.className,
          className
        ),
        ...props,
        children: [
          hideIcon ? null : /* @__PURE__ */ jsx(Icon, { className: "h-3.5 w-3.5", "aria-hidden": "true" }),
          /* @__PURE__ */ jsxs("span", { children: [
            /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Risk level: " }),
            text
          ] })
        ]
      }
    );
  }
);
RiskBadge.displayName = "RiskBadge";
var statusDotClass = {
  online: "bg-success shadow-[0_0_10px_1px_hsl(var(--success)/0.7)]",
  degraded: "bg-warning shadow-[0_0_10px_1px_hsl(var(--warning)/0.6)]",
  offline: "bg-critical shadow-[0_0_10px_1px_hsl(var(--critical)/0.7)]",
  unknown: "bg-muted-foreground"
};
var statusLabel = {
  online: "Online",
  degraded: "Degraded",
  offline: "Offline",
  unknown: "Unknown"
};
var StatusIndicator = forwardRef(
  ({ status, label, pulse = false, className, ...props }, ref) => {
    const text = label ?? statusLabel[status];
    return /* @__PURE__ */ jsxs(
      "span",
      {
        ref,
        className: cn("inline-flex items-center gap-2 text-sm text-foreground", className),
        role: "status",
        "aria-label": text,
        ...props,
        children: [
          /* @__PURE__ */ jsxs("span", { className: "relative flex h-2.5 w-2.5 shrink-0", "aria-hidden": "true", children: [
            pulse ? /* @__PURE__ */ jsx(
              "span",
              {
                className: cn(
                  "absolute inline-flex h-full w-full animate-pulse-ring rounded-full",
                  statusDotClass[status]
                )
              }
            ) : null,
            /* @__PURE__ */ jsx("span", { className: cn("relative inline-flex h-2.5 w-2.5 rounded-full", statusDotClass[status]) })
          ] }),
          text ? /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: text }) : null
        ]
      }
    );
  }
);
StatusIndicator.displayName = "StatusIndicator";
var accentStyle = {
  primary: "text-context-primary",
  success: "text-success",
  warning: "text-warning",
  critical: "text-critical",
  info: "text-info",
  ai: "text-ai",
  brand: "text-brand"
};
var impactStyle = {
  favorable: "text-success",
  unfavorable: "text-critical",
  unknown: "text-muted-foreground"
};
var trendIcon = {
  up: ArrowUpRight,
  down: ArrowDownRight,
  flat: ArrowRight
};
function MetricCard({
  label,
  value,
  unavailableLabel = "Unavailable",
  unit,
  hint,
  delta,
  trend = "flat",
  impact = "unknown",
  prior,
  accent = "primary",
  icon,
  onClick,
  className
}) {
  const TrendIcon = trendIcon[trend];
  const interactive = typeof onClick === "function";
  const unavailable = value === null;
  const body = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-2", children: [
      /* @__PURE__ */ jsx("p", { className: "text-2xs text-muted-foreground min-w-0 font-medium uppercase tracking-wider", children: label }),
      icon ? /* @__PURE__ */ jsx(
        "span",
        {
          className: cn(
            "flex h-6 w-6 shrink-0 items-center justify-center [&_svg]:h-4 [&_svg]:w-4",
            accentStyle[accent]
          ),
          children: icon
        }
      ) : null
    ] }),
    /* @__PURE__ */ jsxs("p", { className: "mt-2 flex flex-wrap items-baseline gap-x-1 text-[clamp(1.2rem,1.7vw,1.6rem)] font-semibold tabular-nums leading-none tracking-tight [overflow-wrap:anywhere]", children: [
      /* @__PURE__ */ jsx("span", { className: unavailable ? "text-muted-foreground" : "text-foreground", children: unavailable ? unavailableLabel : value }),
      !unavailable && unit ? /* @__PURE__ */ jsx("span", { className: "text-muted-foreground text-sm font-medium", children: unit }) : null
    ] }),
    !unavailable && delta || prior ? /* @__PURE__ */ jsxs("div", { className: "mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs", children: [
      !unavailable && delta ? /* @__PURE__ */ jsxs(
        "span",
        {
          "data-impact": impact,
          className: cn("inline-flex items-center gap-1 font-medium", impactStyle[impact]),
          children: [
            /* @__PURE__ */ jsx(TrendIcon, { className: "h-3.5 w-3.5", "aria-hidden": "true" }),
            delta
          ]
        }
      ) : null,
      prior ? /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: prior }) : null
    ] }) : null,
    hint ? /* @__PURE__ */ jsx("p", { className: "text-muted-foreground mt-3 text-xs", children: hint }) : null
  ] });
  const shell = "glass hx-data-surface relative block w-full rounded-lg p-4 text-left text-card-foreground";
  if (interactive) {
    return /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "data-data-surface": "metric",
        "data-native-inspection": "",
        onClick,
        className: cn(
          shell,
          "hover:bg-secondary/40 focus-visible:ring-ring focus-visible:ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          className
        ),
        children: body
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { "data-data-surface": "metric", className: cn(shell, className), children: [
    body,
    /* @__PURE__ */ jsx(SurfaceInspectButton, { title: label })
  ] });
}
var headingVariants = cva("font-semibold text-foreground", {
  variants: {
    level: {
      1: "text-3xl",
      2: "text-2xl",
      3: "text-xl",
      4: "text-lg",
      5: "text-base",
      6: "text-sm uppercase tracking-widest text-muted-foreground"
    }
  },
  defaultVariants: {
    level: 2
  }
});
var Heading = forwardRef(
  ({ className, level = 2, as, ...props }, ref) => {
    const Tag = as ?? `h${level ?? 2}`;
    return /* @__PURE__ */ jsx(Tag, { ref, className: cn(headingVariants({ level }), className), ...props });
  }
);
Heading.displayName = "Heading";
var textVariants = cva("", {
  variants: {
    size: {
      xs: "text-xs",
      sm: "text-sm",
      base: "text-base",
      md: "text-md",
      lg: "text-lg"
    },
    tone: {
      default: "text-foreground",
      muted: "text-muted-foreground",
      subtle: "text-muted-foreground/70",
      primary: "text-primary",
      success: "text-success",
      warning: "text-warning",
      critical: "text-critical"
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold"
    },
    mono: {
      true: "font-mono tabular-nums",
      false: ""
    }
  },
  defaultVariants: {
    size: "sm",
    tone: "default",
    weight: "normal",
    mono: false
  }
});
var Text = forwardRef(
  ({ className, size, tone, weight, mono, as = "p", ...props }, ref) => {
    const Tag = as;
    return /* @__PURE__ */ jsx(
      Tag,
      {
        ref,
        className: cn(textVariants({ size, tone, weight, mono }), className),
        ...props
      }
    );
  }
);
Text.displayName = "Text";
var Eyebrow = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "p",
    {
      ref,
      className: cn("hx-eyebrow text-2xs font-semibold uppercase tracking-widest", className),
      ...props
    }
  )
);
Eyebrow.displayName = "Eyebrow";
var fieldControlClass = "scheme-dark box-border h-[var(--helios-control-height,28px)] rounded-md border border-white/10 bg-white/[0.04] px-2 py-0 text-xs leading-4 text-foreground outline-none transition-[border-color,box-shadow,background-color] duration-200 placeholder:text-muted-foreground/70 hover:border-white/20 focus:border-ring focus:bg-white/[0.06] focus:ring-0 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-0";
var controlRowClass = "flex flex-wrap items-center gap-2";
var fieldBase = "flex h-[var(--helios-input-height,28px)] w-full items-center gap-2 rounded-md border bg-white/[0.04] px-2 py-0 text-xs leading-4 transition-[border-color,box-shadow,background-color] duration-200 hover:border-white/20 focus-within:border-ring focus-within:bg-white/[0.06] focus-within:shadow-[0_0_0_3px_hsl(var(--ring)/0.16)] focus-within:outline-none focus-within:ring-0";
var Input = forwardRef(
  ({ className, startAdornment, endAdornment, invalid, disabled, ...props }, ref) => /* @__PURE__ */ jsxs(
    "div",
    {
      "data-invalid": invalid ? "" : void 0,
      className: cn(
        fieldBase,
        invalid ? "border-critical focus-within:border-critical" : "border-white/10",
        disabled && "cursor-not-allowed opacity-60",
        className
      ),
      children: [
        startAdornment ? /* @__PURE__ */ jsx("span", { className: "text-muted-foreground pointer-events-none shrink-0", children: startAdornment }) : null,
        /* @__PURE__ */ jsx(
          "input",
          {
            ref,
            disabled,
            "aria-invalid": invalid || void 0,
            className: "text-foreground placeholder:text-muted-foreground/75 h-full min-h-0 w-full bg-transparent py-0 text-xs leading-4 outline-none ring-0 focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed",
            ...props
          }
        ),
        endAdornment ? /* @__PURE__ */ jsx("span", { className: "text-muted-foreground shrink-0", children: endAdornment }) : null
      ]
    }
  )
);
Input.displayName = "Input";
var Kbd = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "kbd",
    {
      ref,
      className: cn(
        "inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-md border border-border/75 bg-background/50 px-1.5 font-mono text-2xs font-medium text-muted-foreground shadow-inner",
        className
      ),
      ...props
    }
  )
);
Kbd.displayName = "Kbd";
var Skeleton = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      "aria-hidden": "true",
      className: cn("hx-shimmer rounded-lg", className),
      ...props
    }
  )
);
Skeleton.displayName = "Skeleton";
var sizeClass = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-6 w-6"
};
var Spinner = forwardRef(
  ({ className, size = "md", label = "Loading", ...props }, ref) => /* @__PURE__ */ jsxs("span", { ref, role: "status", "aria-label": label, className: cn("inline-flex", className), ...props, children: [
    /* @__PURE__ */ jsx(
      Loader2,
      {
        className: cn("animate-spin text-brand drop-shadow-[0_0_6px_hsl(var(--brand)/0.7)]", sizeClass[size]),
        "aria-hidden": "true"
      }
    ),
    /* @__PURE__ */ jsx("span", { className: "sr-only", children: label })
  ] })
);
Spinner.displayName = "Spinner";
var EmptyState = forwardRef(
  ({ icon, title, description, action, className, ...props }, ref) => /* @__PURE__ */ jsxs(
    "div",
    {
      ref,
      className: cn(
        "flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-6 py-10 text-center",
        className
      ),
      ...props,
      children: [
        icon ? /* @__PURE__ */ jsx("span", { className: "flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.06] text-muted-foreground ring-1 ring-white/10", children: icon }) : null,
        /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
          /* @__PURE__ */ jsx("p", { className: "text-sm font-semibold text-foreground", children: title }),
          description ? /* @__PURE__ */ jsx("p", { className: "mx-auto max-w-sm text-xs text-muted-foreground", children: description }) : null
        ] }),
        action ? /* @__PURE__ */ jsx("div", { className: "mt-1 flex items-center gap-2", children: action }) : null
      ]
    }
  )
);
EmptyState.displayName = "EmptyState";
var ErrorState = forwardRef(
  ({ title = "Something went wrong", description, action, className, ...props }, ref) => /* @__PURE__ */ jsxs(
    "div",
    {
      ref,
      role: "alert",
      className: cn(
        "flex flex-col items-center justify-center gap-3 rounded-lg border border-critical/30 bg-critical/[0.06] px-6 py-10 text-center",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx("span", { className: "flex h-11 w-11 items-center justify-center rounded-full bg-critical/15 text-critical shadow-glow-critical", children: /* @__PURE__ */ jsx(CircleAlert, { className: "h-5 w-5", "aria-hidden": "true" }) }),
        /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
          /* @__PURE__ */ jsx("p", { className: "text-sm font-semibold text-foreground", children: title }),
          description ? /* @__PURE__ */ jsx("p", { className: "mx-auto max-w-sm text-xs text-muted-foreground", children: description }) : null
        ] }),
        action ? /* @__PURE__ */ jsx("div", { className: "mt-1 flex items-center gap-2", children: action }) : null
      ]
    }
  )
);
ErrorState.displayName = "ErrorState";
var Panel = forwardRef(
  ({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
    "div",
    {
      ref,
      "data-data-surface": "panel",
      "data-native-inspection": props.onClick ? "" : void 0,
      className: cn("glass hx-data-surface text-card-foreground rounded-lg p-4", className),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx(SurfaceInspectButton, { native: Boolean(props.onClick) })
      ]
    }
  )
);
Panel.displayName = "Panel";
var stackGapClass = {
  none: "gap-0",
  sm: "gap-2",
  md: "gap-4",
  lg: "gap-6"
};
var Stack = forwardRef(
  ({ className, gap = "md", ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("flex flex-col", stackGapClass[gap], className), ...props })
);
Stack.displayName = "Stack";
var Toolbar = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      role: "toolbar",
      className: cn("flex flex-row items-center gap-2", className),
      ...props
    }
  )
);
Toolbar.displayName = "Toolbar";
var Container = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn("mx-auto w-full max-w-[1600px] px-3 sm:px-4", className),
      ...props
    }
  )
);
Container.displayName = "Container";
var sectionSpacing = {
  sm: "space-y-4",
  md: "space-y-6",
  lg: "space-y-8"
};
var Section = forwardRef(
  ({ className, spacing = "md", ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn(sectionSpacing[spacing], className), ...props })
);
Section.displayName = "Section";
var gridColsClass = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 xl:grid-cols-4",
  6: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6",
  12: "grid-cols-2 sm:grid-cols-4 lg:grid-cols-12"
};
var Grid = forwardRef(
  ({ className, cols = 3, gap = "md", ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn("grid", gridColsClass[cols], stackGapClass[gap], className),
      ...props
    }
  )
);
Grid.displayName = "Grid";
var Divider = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx("hr", { ref, className: cn("hx-divider", className), ...props })
);
Divider.displayName = "Divider";
var PageHeader = forwardRef(
  ({ title, description, eyebrow, actions, className, ...props }, ref) => /* @__PURE__ */ jsxs(
    "header",
    {
      ref,
      className: cn(
        "hx-rise relative flex shrink-0 flex-col gap-2 pb-3 sm:flex-row sm:items-end sm:justify-between",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "min-w-0 space-y-0.5", children: [
          eyebrow ? /* @__PURE__ */ jsx("p", { className: "hx-eyebrow text-2xs font-semibold uppercase tracking-widest", children: eyebrow }) : null,
          /* @__PURE__ */ jsx("h1", { className: "hx-gradient-text text-2xl font-semibold tracking-tight", children: title }),
          description ? /* @__PURE__ */ jsx("p", { className: "text-muted-foreground max-w-3xl text-xs", children: description }) : null
        ] }),
        actions ? /* @__PURE__ */ jsx("div", { className: "flex shrink-0 flex-wrap items-center gap-1.5", children: actions }) : null,
        /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": "true",
            className: "hx-divider pointer-events-none absolute inset-x-0 bottom-0"
          }
        )
      ]
    }
  )
);
PageHeader.displayName = "PageHeader";
var TooltipProvider = TooltipPrimitive.Provider;
var Tooltip = TooltipPrimitive.Root;
var TooltipTrigger = TooltipPrimitive.Trigger;
var TooltipContent = forwardRef(({ className, sideOffset = 6, ...props }, ref) => /* @__PURE__ */ jsx(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsx(
  TooltipPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "scheme-dark bg-popover text-popover-foreground glass-strong z-popover overflow-hidden rounded-lg px-2.5 py-1.5 text-xs",
      "data-[state=delayed-open]:animate-fade-in data-[state=closed]:animate-fade-out",
      className
    ),
    ...props
  }
) }));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
var DropdownMenu = DropdownMenuPrimitive.Root;
var DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
var DropdownMenuGroup = DropdownMenuPrimitive.Group;
var DropdownMenuPortal = DropdownMenuPrimitive.Portal;
var DropdownMenuContent = forwardRef(({ className, sideOffset = 6, align = "end", ...props }, ref) => /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn(
      "scheme-dark bg-popover text-popover-foreground glass-strong z-popover min-w-52 overflow-hidden rounded-xl p-1.5",
      "data-[state=open]:animate-zoom-in data-[state=closed]:animate-fade-out",
      className
    ),
    ...props
  }
) }));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
var DropdownMenuItem = forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Item,
  {
    ref,
    className: cn(
      "text-foreground relative flex cursor-pointer select-none items-center gap-2 rounded-md px-2.5 py-2 text-sm outline-none transition-colors",
      "focus:bg-primary/15 focus:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
var DropdownMenuLabel = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Label,
  {
    ref,
    className: cn(
      "text-2xs text-muted-foreground px-2.5 py-1.5 font-semibold uppercase tracking-wider",
      className
    ),
    ...props
  }
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
var DropdownMenuSeparator = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Separator,
  {
    ref,
    className: cn("hx-divider -mx-1 my-1", className),
    ...props
  }
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
var DropdownMenuCheckboxItem = forwardRef(({ className, children, checked, ...props }, ref) => /* @__PURE__ */ jsxs(
  DropdownMenuPrimitive.CheckboxItem,
  {
    ref,
    checked,
    className: cn(
      "text-foreground relative flex cursor-pointer select-none items-center rounded-md py-2 pl-8 pr-2.5 text-sm outline-none transition-colors",
      "focus:bg-primary/15 focus:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2.5 flex h-4 w-4 items-center justify-center", children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Check, { className: "h-4 w-4", "aria-hidden": "true" }) }) }),
      children
    ]
  }
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;
var DropdownMenuSub = DropdownMenuPrimitive.Sub;
var DropdownMenuSubTrigger = forwardRef(({ className, inset, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  DropdownMenuPrimitive.SubTrigger,
  {
    ref,
    className: cn(
      "text-foreground relative flex cursor-pointer select-none items-center gap-2 rounded-md px-2.5 py-2 text-sm outline-none transition-colors",
      "focus:bg-primary/15 data-[state=open]:bg-primary/15",
      inset && "pl-8",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx(ChevronRight, { className: "text-muted-foreground ml-auto h-4 w-4", "aria-hidden": "true" })
    ]
  }
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
var DropdownMenuSubContent = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.SubContent,
  {
    ref,
    className: cn(
      "scheme-dark bg-popover text-popover-foreground glass-strong z-popover min-w-52 overflow-hidden rounded-xl p-1.5",
      "data-[state=open]:animate-zoom-in data-[state=closed]:animate-fade-out",
      className
    ),
    ...props
  }
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
var Sheet = DialogPrimitive.Root;
var SheetTrigger = DialogPrimitive.Trigger;
var SheetClose = DialogPrimitive.Close;
var SheetPortal = DialogPrimitive.Portal;
var SheetOverlay = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn(
      "z-overlay fixed inset-0 bg-black/65 backdrop-blur-[6px]",
      "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
      className
    ),
    ...props
  }
));
SheetOverlay.displayName = "SheetOverlay";
var sheetVariants = cva(
  "scheme-dark bg-popover text-popover-foreground glass-strong fixed z-modal flex flex-col rounded-none border-white/10 outline-none",
  {
    variants: {
      side: {
        left: "inset-y-0 left-0 h-full w-80 max-w-[85vw] border-r data-[state=open]:animate-slide-in-left data-[state=closed]:animate-slide-out-left",
        right: "inset-y-0 right-0 h-full w-96 max-w-[90vw] border-l data-[state=open]:animate-slide-in-right data-[state=closed]:animate-slide-out-right"
      }
    },
    defaultVariants: { side: "right" }
  }
);
var SheetContent = forwardRef(({ className, children, side = "right", hideClose = false, ...props }, ref) => /* @__PURE__ */ jsxs(SheetPortal, { children: [
  /* @__PURE__ */ jsx(SheetOverlay, {}),
  /* @__PURE__ */ jsxs(
    DialogPrimitive.Content,
    {
      ref,
      className: cn(sheetVariants({ side }), className),
      ...props,
      "data-native-modal-boundary": "",
      children: [
        /* @__PURE__ */ jsx(SurfaceInspectionScope, { value: null, children }),
        hideClose ? null : /* @__PURE__ */ jsx(
          DialogPrimitive.Close,
          {
            className: "text-muted-foreground hover:bg-secondary hover:text-foreground absolute right-4 top-4 rounded-full p-1 transition-colors focus-visible:outline-none",
            "aria-label": "Close",
            children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4", "aria-hidden": "true" })
          }
        )
      ]
    }
  )
] }));
SheetContent.displayName = "SheetContent";
function SheetHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn("hx-section-header relative flex flex-col gap-1 p-4", className),
      ...props
    }
  );
}
function SheetFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("border-border mt-auto border-t p-4", className), ...props });
}
var SheetTitle = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Title,
  {
    ref,
    className: cn("hx-section-title text-base font-semibold tracking-tight", className),
    ...props
  }
));
SheetTitle.displayName = "SheetTitle";
var SheetDescription = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-muted-foreground text-xs", className),
    ...props
  }
));
SheetDescription.displayName = "SheetDescription";
var Popover = PopoverPrimitive.Root;
var PopoverTrigger = PopoverPrimitive.Trigger;
var PopoverAnchor = PopoverPrimitive.Anchor;
var PopoverClose = PopoverPrimitive.Close;
var PopoverContent = forwardRef(({ className, align = "start", sideOffset = 6, ...props }, ref) => /* @__PURE__ */ jsx(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx(
  PopoverPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn(
      "scheme-dark bg-popover text-popover-foreground glass-strong z-popover w-72 rounded-xl p-3 outline-none",
      "data-[state=open]:animate-zoom-in data-[state=closed]:animate-fade-out",
      className
    ),
    ...props
  }
) }));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;
var ScrollArea = forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(ScrollAreaPrimitive.Root, { ref, className: cn("relative overflow-hidden", className), ...props, children: [
  /* @__PURE__ */ jsx(ScrollAreaPrimitive.Viewport, { className: "h-full w-full rounded-[inherit]", children }),
  /* @__PURE__ */ jsx(ScrollBar, {}),
  /* @__PURE__ */ jsx(ScrollAreaPrimitive.Corner, {})
] }));
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;
var ScrollBar = forwardRef(({ className, orientation = "vertical", ...props }, ref) => /* @__PURE__ */ jsx(
  ScrollAreaPrimitive.ScrollAreaScrollbar,
  {
    ref,
    orientation,
    className: cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" && "h-full w-2 border-l border-l-transparent p-px",
      orientation === "horizontal" && "h-2 flex-col border-t border-t-transparent p-px",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx(ScrollAreaPrimitive.ScrollAreaThumb, { className: "relative flex-1 rounded-full bg-border" })
  }
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;
var Separator2 = forwardRef(({ className, orientation = "horizontal", decorative = true, ...props }, ref) => /* @__PURE__ */ jsx(
  SeparatorPrimitive.Root,
  {
    ref,
    decorative,
    orientation,
    className: cn(
      "shrink-0 bg-border",
      orientation === "horizontal" ? "h-px w-full" : "h-full w-px",
      className
    ),
    ...props
  }
));
Separator2.displayName = SeparatorPrimitive.Root.displayName;
var Avatar = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AvatarPrimitive.Root,
  {
    ref,
    className: cn(
      "relative flex h-8 w-8 shrink-0 overflow-hidden rounded-full border border-border",
      className
    ),
    ...props
  }
));
Avatar.displayName = AvatarPrimitive.Root.displayName;
var AvatarImage = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(AvatarPrimitive.Image, { ref, className: cn("aspect-square h-full w-full", className), ...props }));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;
var AvatarFallback = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AvatarPrimitive.Fallback,
  {
    ref,
    className: cn(
      "flex h-full w-full items-center justify-center bg-secondary text-xs font-semibold text-foreground",
      className
    ),
    ...props
  }
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;
var VisuallyHidden = VisuallyHiddenPrimitive.Root;
var Command = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  Command$1,
  {
    ref,
    className: cn(
      "scheme-dark text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-xl bg-transparent",
      className
    ),
    ...props
  }
));
Command.displayName = "Command";
var CommandInput = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsxs("div", { className: "border-border flex items-center gap-2 border-b px-3", "cmdk-input-wrapper": "", children: [
  /* @__PURE__ */ jsx(Search, { className: "text-muted-foreground h-4 w-4 shrink-0", "aria-hidden": "true" }),
  /* @__PURE__ */ jsx(
    Command$1.Input,
    {
      ref,
      className: cn(
        "text-foreground placeholder:text-muted-foreground flex h-11 w-full bg-transparent py-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      ...props
    }
  )
] }));
CommandInput.displayName = "CommandInput";
var CommandList = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  Command$1.List,
  {
    ref,
    className: cn("max-h-80 overflow-y-auto overflow-x-hidden p-1", className),
    ...props
  }
));
CommandList.displayName = "CommandList";
var CommandEmpty = forwardRef((props, ref) => /* @__PURE__ */ jsx(
  Command$1.Empty,
  {
    ref,
    className: "text-muted-foreground py-8 text-center text-sm",
    ...props
  }
));
CommandEmpty.displayName = "CommandEmpty";
var CommandGroup = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  Command$1.Group,
  {
    ref,
    className: cn(
      "text-foreground [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider",
      className
    ),
    ...props
  }
));
CommandGroup.displayName = "CommandGroup";
var CommandItem = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  Command$1.Item,
  {
    ref,
    className: cn(
      "text-foreground relative flex cursor-pointer select-none items-center gap-2.5 rounded-md px-2.5 py-2 text-sm outline-none",
      "data-[selected=true]:bg-primary/15 data-[selected=true]:text-foreground data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
      className
    ),
    ...props
  }
));
CommandItem.displayName = "CommandItem";
var CommandSeparator = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  Command$1.Separator,
  {
    ref,
    className: cn("bg-border -mx-1 my-1 h-px", className),
    ...props
  }
));
CommandSeparator.displayName = "CommandSeparator";
function CommandDialog({
  open,
  onOpenChange,
  title = "Command palette",
  description = "Search and jump to any area of HELIOS ConstructionOS.",
  children,
  shouldFilter = true
}) {
  return /* @__PURE__ */ jsx(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsxs(
    DialogContent,
    {
      hideClose: true,
      className: "max-w-xl gap-0 overflow-hidden p-0",
      "aria-describedby": void 0,
      children: [
        /* @__PURE__ */ jsx(DialogTitle, { className: "sr-only", children: title }),
        /* @__PURE__ */ jsx(DialogDescription, { className: "sr-only", children: description }),
        /* @__PURE__ */ jsx(Command, { shouldFilter, children })
      ]
    }
  ) });
}
function Toaster(props) {
  return /* @__PURE__ */ jsx(
    Toaster$1,
    {
      theme: "dark",
      position: "bottom-right",
      toastOptions: {
        classNames: {
          toast: "scheme-dark bg-popover text-popover-foreground glass-strong group rounded-xl",
          title: "text-sm font-medium text-foreground",
          description: "text-xs text-muted-foreground",
          actionButton: "rounded-md bg-gradient-primary px-2.5 py-1 text-xs font-medium text-primary-foreground",
          cancelButton: "rounded-md bg-white/10 px-2.5 py-1 text-xs font-medium text-foreground",
          success: "text-success",
          error: "text-critical",
          warning: "text-warning",
          info: "text-info"
        }
      },
      ...props
    }
  );
}

export { Avatar, AvatarFallback, AvatarImage, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, Container, Divider, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, EmptyState, ErrorState, Eyebrow, Grid, Heading, IconButton, Input, Kbd, MetricCard, PageHeader, Panel, Popover, PopoverAnchor, PopoverClose, PopoverContent, PopoverTrigger, RiskBadge, ScrollArea, ScrollBar, Section, Separator2 as Separator, Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetOverlay, SheetPortal, SheetTitle, SheetTrigger, Skeleton, Spinner, Stack, StatusIndicator, Surface, Text, Toaster, Toolbar, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, VisuallyHidden, controlRowClass, fieldControlClass, iconButtonVariants, surfaceVariants };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map